// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/work-optimization/OptimizationInsightPanel.jsx

import React from "react";
import EstimatedEffortBadge from "./EstimatedEffortBadge";
import BatchOpportunityCard from "./BatchOpportunityCard";
import DependencyInsightCard from "./DependencyInsightCard";
import DuplicateWorkCard from "./DuplicateWorkCard";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function firstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

function scoreValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : "—";
}

function normalizeTaskId(value) {
  const text = clean(value);
  if (!text) return "";
  return text.startsWith("task:") ? text.slice(5) : text;
}

function idCandidates(item = {}) {
  const rawTask = item?.raw_task && typeof item.raw_task === "object" ? item.raw_task : {};
  return [
    item?.work_item_id,
    item?.task_id,
    item?.taskId,
    item?.remediation_task_id,
    item?.remediationTaskId,
    item?.id,
    rawTask?.id,
    rawTask?.task_id,
  ]
    .map(normalizeTaskId)
    .filter(Boolean);
}

function taskIdSet(item = {}) {
  return new Set(idCandidates(item));
}

function sameTask(left = {}, right = {}) {
  const leftIds = idCandidates(left);
  const rightIds = taskIdSet(right);
  return leftIds.some((id) => rightIds.has(id));
}

function findForTask(items = [], task = {}) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) return null;

  const direct = rows.find((item) => sameTask(item, task));
  if (direct) return direct;

  const taskRule = clean(task?.rule_id || task?.ruleId || task?.raw_task?.rule_id).toUpperCase();
  const taskResource = clean(task?.resource_id || task?.resourceId || task?.raw_task?.resource_id).toLowerCase();

  return (
    rows.find((item) => {
      const itemRule = clean(item?.rule_id || item?.ruleId || item?.raw_task?.rule_id).toUpperCase();
      const itemResource = clean(item?.resource_id || item?.resourceId || item?.raw_task?.resource_id).toLowerCase();
      return Boolean(taskRule && itemRule === taskRule && (!taskResource || itemResource === taskResource));
    }) || null
  );
}

function findBatchForTask(batches = [], task = {}) {
  const rows = Array.isArray(batches) ? batches : [];
  if (!rows.length) return null;

  const taskIds = taskIdSet(task);

  return (
    rows.find((batch) => {
      const ids = Array.isArray(batch?.task_ids) ? batch.task_ids.map(normalizeTaskId).filter(Boolean) : [];
      return ids.some((id) => taskIds.has(id));
    }) || rows[0] || null
  );
}

function findDependencyForTask(dependencies = [], task = {}) {
  return findForTask(dependencies, task) || (Array.isArray(dependencies) ? dependencies[0] : null);
}

function findDuplicateForTask(duplicates = [], task = {}) {
  const rows = Array.isArray(duplicates) ? duplicates : [];
  const taskIds = taskIdSet(task);

  return (
    rows.find((duplicate) => {
      const directIds = [
        duplicate?.work_item_id,
        duplicate?.task_id,
        duplicate?.taskId,
        duplicate?.id,
        ...(Array.isArray(duplicate?.task_ids) ? duplicate.task_ids : []),
        ...(Array.isArray(duplicate?.duplicate_task_ids) ? duplicate.duplicate_task_ids : []),
      ]
        .map(normalizeTaskId)
        .filter(Boolean);

      return directIds.some((id) => taskIds.has(id)) || sameTask(duplicate, task);
    }) || rows[0] || null
  );
}

function taskOptimizationFromContext(context = {}) {
  const task = context?.task && typeof context.task === "object" ? context.task : null;
  if (!task) return context?.optimization || null;
  return task.optimization || context.optimization || null;
}

function itemReasons(...items) {
  const values = [];
  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    [
      item.reasons,
      item.why,
      item.explanations,
      item.priority_reasons,
      item.operator_reasons,
    ].forEach((candidate) => {
      if (Array.isArray(candidate)) values.push(...candidate);
    });
  });

  const seen = new Set();
  return values
    .map(clean)
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function categoryLabel(matches = {}, optimization = {}) {
  if (matches.quickWin) return "Quick Win";
  if (matches.fastFollow) return "Fast Follow";
  if (matches.highImpact) return "High Impact";
  if (matches.batch) return "Batch Opportunity";
  if (matches.duplicate) return "Duplicate Work Signal";
  if (matches.dependency) return "Dependency-Aware Work";
  return optimization.label || "Optimized Work";
}

function categoryNote(matches = {}, optimization = {}) {
  return clean(
    matches.quickWin?.operator_note ||
      matches.fastFollow?.operator_note ||
      matches.highImpact?.operator_note ||
      matches.batch?.operator_note ||
      matches.dependency?.operator_note ||
      matches.duplicate?.operator_note ||
      optimization.operator_note ||
      optimization.why_now
  );
}

function optimizationScores(optimization = {}, matches = {}) {
  const primary = matches.quickWin || matches.fastFollow || matches.highImpact || {};
  return {
    optimization_score: optimization.optimization_score ?? primary.optimization_score ?? primary.score,
    readiness_score: optimization.readiness_score ?? primary.readiness_score,
    dependency_score: optimization.dependency_score ?? primary.dependency_score,
    impact_score: optimization.impact_score ?? primary.impact_score,
    estimated_effort: optimization.estimated_effort ?? primary.estimated_effort,
  };
}

function matchedTaskContext(safeContext = {}, task = {}) {
  const quickWins = firstArray(safeContext.quick_wins);
  const fastFollows = firstArray(safeContext.fast_follows);
  const highImpact = firstArray(safeContext.high_impact);
  const batches = firstArray(safeContext.batches);
  const dependencies = firstArray(safeContext.dependencies);
  const duplicates = firstArray(safeContext.duplicates);

  return {
    quickWin: findForTask(quickWins, task),
    fastFollow: findForTask(fastFollows, task),
    highImpact: findForTask(highImpact, task),
    batch: findBatchForTask(batches, task),
    dependency: findDependencyForTask(dependencies, task),
    duplicate: findDuplicateForTask(duplicates, task),
    hasAny: quickWins.length || fastFollows.length || highImpact.length || batches.length || dependencies.length || duplicates.length,
  };
}

export default function OptimizationInsightPanel({ context, task, loading = false, error = "", compact = false, onOpenTask, onRefresh }) {
  const safeContext = context && typeof context === "object" ? context : {};
  const safeTask = task && typeof task === "object" ? task : {};
  const optimization = taskOptimizationFromContext(safeContext) || safeTask?.optimization || {};
  const matches = matchedTaskContext(safeContext, safeTask);
  const scores = optimizationScores(optimization, matches);

  const reasons = firstArray(
    optimization.reasons,
    optimization.why,
    itemReasons(matches.quickWin, matches.fastFollow, matches.highImpact, matches.batch, matches.dependency, matches.duplicate),
  );

  if (loading) {
    return (
      <section className="wo-insight-panel" aria-label="Optimization insight">
        <span>Optimization Insight</span>
        <h3>Loading work optimization…</h3>
        <p>OASYS is reading advisory sequencing context for this task.</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="wo-insight-panel wo-insight-error" aria-label="Optimization insight error">
        <span>Optimization Insight</span>
        <h3>Optimization context unavailable</h3>
        <p>{error}</p>
      </section>
    );
  }

  if (!optimization && !matches.hasAny) {
    return (
      <section className="wo-insight-panel" aria-label="Optimization insight">
        <span>Optimization Insight</span>
        <h3>No task-specific optimization insight yet</h3>
        <p>OASYS has not found task-specific sequencing, batching, or duplicate-work signals for this work item.</p>
      </section>
    );
  }

  const category = categoryLabel(matches, optimization);
  const note = categoryNote(matches, optimization);

  return (
    <section className={`wo-insight-panel ${compact ? "wo-insight-compact" : ""}`} aria-label="Optimization insight">
      <div className="wo-insight-header">
        <div>
          <span>Optimization Insight</span>
          <h3>{category}</h3>
          <p>{note || "Advisory sequencing based on effort, risk impact, readiness, and dependencies."}</p>
        </div>
        <div className="wo-insight-actions">
          <EstimatedEffortBadge effort={scores.estimated_effort} />
          {onRefresh ? (
            <button type="button" className="mec-link-button" onClick={onRefresh}>
              Refresh
            </button>
          ) : null}
        </div>
      </div>

      <div className="wo-score-grid">
        <div><span>Optimization</span><strong>{scoreValue(scores.optimization_score)}</strong></div>
        <div><span>Readiness</span><strong>{scoreValue(scores.readiness_score)}</strong></div>
        <div><span>Dependency</span><strong>{scoreValue(scores.dependency_score)}</strong></div>
        <div><span>Impact</span><strong>{scoreValue(scores.impact_score)}</strong></div>
      </div>

      {reasons.length ? (
        <div className="wo-reason-block">
          <span>Why This Order</span>
          <ul className="wo-reasons">
            {reasons.slice(0, 4).map((reason, index) => <li key={`optimization-reason-${index}`}>{reason}</li>)}
          </ul>
        </div>
      ) : null}

      {matches.batch ? <BatchOpportunityCard batch={matches.batch} activeTask={safeTask} onOpenTask={onOpenTask} /> : null}
      {matches.dependency ? <DependencyInsightCard insight={matches.dependency} /> : null}
      {matches.duplicate ? <DuplicateWorkCard duplicate={matches.duplicate} /> : null}

      <small className="wo-guardrail">Advisory only. OASYS does not assign, close, validate, merge, or reorder work permanently.</small>
    </section>
  );
}
